import React, { useState, useEffect, useMemo } from "react";
import "../Articles.css";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { useHistory } from "react-router-dom";
import {
  Button,
  Empty,
  Form,
  Input,
  Pagination,
  Select,
  Spin,
  Tag,
} from "antd";
import {
  ArrowRightOutlined,
  BookOutlined,
  LoadingOutlined,
  ReadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { debounce } from "lodash";

import articles from "../../../redux/articles";

const PAGE_SIZE_OPTIONS = ["10", "20", "50", "100"];
const DEBOUNCE_MS = 600;

const TYPE_OPTIONS = [
  { value: "protocol", label: "Clinical Protocols" },
  { value: "training", label: "Training" },
];

// TODO(VER-563 follow-up): caseCategoryId is dead state — nothing in the
// codebase dispatches setCaseCategoryId with a value, so it's always
// undefined. The conditional teleMedicineCategory filtering, the
// initialValues pre-fill, and the connect mapping all serve a feature that
// has no entry point. Either wire up a producer (probably from case-view,
// when a provider clicks "articles for this case") or rip the plumbing out
// here and in redux/articles/{types,actions,reducer}.js.
const ArticleList = ({
  clinicalId,
  trainingId,
  actions,
  articleListLoading,
  articleList,
  articleListCount,
  telemedicineCategoriesList,
  caseCategoryId,
}) => {
  const [form] = Form.useForm();
  const [type, setType] = useState("protocol");
  const [filterParams, setFilterParams] = useState();
  const [tablePageSize, setTablePageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const history = useHistory();
  const isProtocol = type === "protocol";
  const articleCategoryId = isProtocol ? clinicalId : trainingId;

  // Reload whenever the active type / category id / telemedicine preset changes.
  useEffect(() => {
    if (!articleCategoryId) return;
    const filter = {
      page: 1,
      limit: 10,
      articleCategory: articleCategoryId,
      ...(isProtocol && caseCategoryId
        ? { teleMedicineCategory: caseCategoryId }
        : {}),
    };
    setCurrentPage(1);
    setTablePageSize(10);
    setFilterParams(undefined);
    actions.getArticleList(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, articleCategoryId, caseCategoryId]);

  const runFilter = () => {
    if (!articleCategoryId) return;
    const data = form.getFieldsValue();
    const filter = {
      page: 1,
      limit: 10,
      articleCategory: articleCategoryId,
      title: data.search,
    };
    if (isProtocol) {
      if (data.category) {
        filter.teleMedicineCategory = data.category;
      } else if (caseCategoryId) {
        filter.teleMedicineCategory = caseCategoryId;
      }
    }
    setCurrentPage(1);
    setTablePageSize(10);
    setFilterParams(filter);
    actions.getArticleList(filter);
  };

  // useMemo (not useCallback) so the debounce wrapper is created once per dep
  // change and reused across renders. Pair with the cleanup effect below so
  // pending invocations from the previous wrapper are cancelled when deps
  // change or the component unmounts — otherwise switching tabs mid-typing
  // would let a stale-closure request fire 600ms later.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFilter = useMemo(
    () => debounce(runFilter, DEBOUNCE_MS),
    [articleCategoryId, caseCategoryId, isProtocol]
  );

  useEffect(() => () => debouncedFilter.cancel(), [debouncedFilter]);

  const clearFilter = () => {
    form.resetFields();
    setCurrentPage(1);
    setTablePageSize(10);
    setFilterParams(undefined);
    actions.setCaseCategoryId();
    if (articleCategoryId) {
      actions.getArticleList({
        page: 1,
        limit: 10,
        articleCategory: articleCategoryId,
      });
    }
  };

  const handleTypeChange = (next) => {
    // Clear category filter when switching types — telemedicine categories
    // are only relevant for protocols.
    form.setFieldsValue({ category: undefined });
    actions.setCaseCategoryId();
    setType(next);
  };

  const buildPaginatedParams = (page, size) => {
    const params = { ...(filterParams || {}), page, limit: size };
    params.articleCategory = articleCategoryId;
    if (isProtocol && caseCategoryId && !params.teleMedicineCategory) {
      params.teleMedicineCategory = caseCategoryId;
    }
    return params;
  };

  const onChangePage = (page, pageSize) => {
    setCurrentPage(page);
    actions.getArticleList(buildPaginatedParams(page, pageSize));
  };

  const onPageSizeChange = (current, size) => {
    setTablePageSize(size);
    setCurrentPage(1);
    actions.getArticleList(buildPaginatedParams(current, size));
  };

  const openArticle = (id) =>
    history.push(`/dashboard/physician/article-view/${id}`);

  const total = articleListCount ?? 0;
  const ctaLabel = isProtocol ? "View Protocol" : "View Training";
  const CategoryIcon = isProtocol ? BookOutlined : ReadOutlined;

  return (
    <Spin
      size="large"
      spinning={articleListLoading}
      indicator={<LoadingOutlined spin />}
    >
      <div className="articles-toolbar">
        <Form
          form={form}
          layout="inline"
          className="articles-filter-form"
          initialValues={{ category: caseCategoryId }}
        >
          <Form.Item className="articles-filter-item articles-filter-type">
            <Select
              size="large"
              value={type}
              onChange={handleTypeChange}
              options={TYPE_OPTIONS}
              getPopupContainer={(trigger) => trigger.parentElement}
            />
          </Form.Item>

          <Form.Item
            name="search"
            className="articles-filter-item articles-filter-search"
          >
            <Input
              size="large"
              allowClear
              placeholder="Search by title"
              prefix={<SearchOutlined style={{ color: "#a1a8b0" }} />}
              onChange={debouncedFilter}
              onPressEnter={runFilter}
            />
          </Form.Item>

          {isProtocol && (
            <Form.Item
              name="category"
              className="articles-filter-item articles-filter-category"
            >
              <Select
                size="large"
                allowClear
                placeholder="All categories"
                getPopupContainer={(trigger) => trigger.parentElement}
                onChange={runFilter}
                options={(telemedicineCategoriesList || []).map((data) => ({
                  value: data._id,
                  label: data.name,
                }))}
              />
            </Form.Item>
          )}

          <Form.Item className="articles-filter-item articles-filter-clear">
            <Button
              size="large"
              className="articles-clear-btn"
              onClick={clearFilter}
            >
              Clear
            </Button>
          </Form.Item>
        </Form>
      </div>

      {articleList.length !== 0 ? (
        <>
          <div className="articles-grid">
            {articleList.map((item) => (
              <button
                type="button"
                key={item._id}
                className="article-card"
                onClick={() => openArticle(item._id)}
              >
                <div className="article-card-head">
                  <div className="article-card-icon">
                    <CategoryIcon />
                  </div>
                  {isProtocol && item.teleMedicineCategoryName && (
                    <Tag className="article-card-tag">
                      {item.teleMedicineCategoryName}
                    </Tag>
                  )}
                </div>

                <h3 className="article-card-title">{item.title}</h3>

                <p className="article-card-description">{item.description}</p>

                <div className="article-card-footer">
                  <span className="article-card-cta">
                    {ctaLabel}
                    <ArrowRightOutlined />
                  </span>
                </div>
              </button>
            ))}
          </div>

          <div className="articles-pagination-row">
            <Pagination
              total={total}
              showTotal={(t) =>
                `Total ${t} ${isProtocol ? "Protocols" : "Articles"}`
              }
              current={currentPage}
              pageSize={tablePageSize}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              showSizeChanger
              onChange={onChangePage}
              onShowSizeChange={onPageSizeChange}
            />
          </div>
        </>
      ) : (
        !articleListLoading && (
          <Empty
            className="articles-empty"
            description={
              <span className="articles-empty-text">
                No {isProtocol ? "protocols" : "training articles"} found
              </span>
            }
          />
        )
      )}
    </Spin>
  );
};

const mapStateToProps = (state) => {
  const { articleListLoading, articleList, articleListCount, caseCategoryId } =
    state.articles;
  const { telemedicineCategoriesList } = state.entities;

  return {
    articleListLoading,
    articleList,
    articleListCount,
    telemedicineCategoriesList,
    caseCategoryId,
  };
};

const mapDispatchToProps = (dispatch) => {
  const { getArticleList, setCaseCategoryId } = articles.actions;
  return {
    actions: bindActionCreators(
      {
        getArticleList,
        setCaseCategoryId,
      },
      dispatch
    ),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ArticleList);
